import { createElement, isValidElement, type ReactNode } from 'react';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './constants';
import { dictionaries } from './locales';
import { resolveLanguage } from './resolver';
import { tenantDictionaries } from './tenants';
import type {
	Language,
	LocalizationInitPayload,
	RichTranslateFn,
	SupportedLanguage,
	TranslateFn
} from './types';

const STORAGE_KEY = 'platformLanguage';

type LocalizationServiceStateSnapshot = {
	currentLang: SupportedLanguage;
	disabledLanguages: Language[];
	isEnabled: boolean | undefined;
	logMissingTranslations: boolean;
	storedLanguage: string | null;
	tenant: string | null;
};

class LocalizationServiceImpl {
	private currentLang: SupportedLanguage = DEFAULT_LANGUAGE;
	private listeners = new Set<() => void>();
	private tenant: string | null = null;
	private isEnabled: boolean | undefined;
	private disabledLanguages: Language[] = [];
	private logMissingTranslations = true;

	init({
		isEnabled = false,
		tenant,
		disabledLanguages = [],
		logMissingTranslations = true
	}: Omit<LocalizationInitPayload, 'providedLang'>) {
		this.tenant = tenant?.toLowerCase() ?? null;
		this.isEnabled = isEnabled;
		this.logMissingTranslations = logMissingTranslations;

		this.disabledLanguages = disabledLanguages;

		if (!isEnabled) {
			this.currentLang = DEFAULT_LANGUAGE;
			return;
		}

		const storedLang =
			typeof window !== 'undefined'
				? (localStorage.getItem(STORAGE_KEY) as SupportedLanguage | null)
				: null;

		this.currentLang = resolveLanguage(
			storedLang,
			typeof navigator !== 'undefined' ? navigator.language : undefined
		);

		if (!storedLang) {
			localStorage.setItem(STORAGE_KEY, this.currentLang);
		}
	}

	captureState(): LocalizationServiceStateSnapshot {
		const storedLanguage =
			typeof window === 'undefined' ? null : localStorage.getItem(STORAGE_KEY);

		return {
			currentLang: this.currentLang,
			disabledLanguages: [...this.disabledLanguages],
			isEnabled: this.isEnabled,
			logMissingTranslations: this.logMissingTranslations,
			storedLanguage,
			tenant: this.tenant
		};
	}

	restoreState(snapshot: LocalizationServiceStateSnapshot): void {
		this.currentLang = snapshot.currentLang;
		this.disabledLanguages = [...snapshot.disabledLanguages];
		this.isEnabled = snapshot.isEnabled;
		this.logMissingTranslations = snapshot.logMissingTranslations;
		this.tenant = snapshot.tenant;

		if (typeof window !== 'undefined') {
			if (snapshot.storedLanguage) {
				localStorage.setItem(STORAGE_KEY, snapshot.storedLanguage);
			} else {
				localStorage.removeItem(STORAGE_KEY);
			}
		}

		this.listeners.forEach((listener) => {
			listener();
		});
	}

	getLanguage(): SupportedLanguage {
		return this.currentLang;
	}

	setLanguage(lang: SupportedLanguage) {
		if (!this.isEnabled) {
			this.currentLang = DEFAULT_LANGUAGE;
			return;
		}

		if (this.currentLang === lang) {
			return;
		}

		this.currentLang = lang;

		if (typeof window !== 'undefined') {
			localStorage.setItem(STORAGE_KEY, lang);
		}

		this.listeners.forEach((listener) => {
			listener();
		});
	}

	public getSupportedLanguages(): SupportedLanguage[] {
		return SUPPORTED_LANGUAGES.filter(
			(item) => !this.disabledLanguages.includes(item)
		);
	}

	private resolvePath(obj: unknown, key: string): unknown {
		if (!obj || typeof obj !== 'object') {
			return undefined;
		}

		const record = obj as Record<string, unknown>;

		if (key in record) {
			return record[key];
		}

		if (!key.includes('.')) {
			return undefined;
		}

		return key.split('.').reduce<unknown>((acc, part) => {
			if (!acc || typeof acc !== 'object') {
				return undefined;
			}
			return (acc as Record<string, unknown>)[part];
		}, obj);
	}

	translate: TranslateFn = (namespace, key, params, fallback) => {
		const activeDict =
			dictionaries[this.currentLang] ?? dictionaries[DEFAULT_LANGUAGE];
		const defaultDict = dictionaries[DEFAULT_LANGUAGE];

		const tenantCurrentNamespace =
			this.tenant &&
			tenantDictionaries?.[this.tenant]?.[this.currentLang]?.[namespace];

		const tenantDefaultNamespace =
			this.tenant &&
			tenantDictionaries?.[this.tenant]?.[DEFAULT_LANGUAGE]?.[namespace];

		const activeNamespace = activeDict?.[namespace];
		const fallbackNamespace = defaultDict?.[namespace];

		const tenantCurrentValue = this.resolvePath(tenantCurrentNamespace, key);
		const tenantDefaultValue = this.resolvePath(tenantDefaultNamespace, key);
		const activeValue = this.resolvePath(activeNamespace, key);
		const fallbackValue = this.resolvePath(fallbackNamespace, key);
		const rawValue =
			tenantCurrentValue ?? tenantDefaultValue ?? activeValue ?? fallbackValue;

		if (rawValue === undefined) {
			if (this.logMissingTranslations) {
				console.warn(
					`[i18n] Missing translation → namespace: "${namespace}", key: "${key}", tenant: "${this.tenant}", lang: "${this.currentLang}"`
				);
			}
			return fallback || key;
		}

		if (typeof rawValue !== 'string') {
			return fallback || key;
		}

		let text = rawValue;

		if (params) {
			text = text.replace(/\{\{(.*?)\}\}|\{(.*?)\}/g, (_, p1, p2) => {
				const paramKey = (p1 ?? p2)?.trim();
				return String(params?.[paramKey] ?? '');
			});
		}

		return text;
	};

	rich: RichTranslateFn = (namespace, key, renderers, params, fallback) => {
		const text = this.translate(namespace, key, params, fallback);
		return this.parseRich(text, renderers, { next: 0 });
	};

	subscribe(listener: () => void) {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	}

	private withUniqueKey(
		node: ReactNode,
		keyCounter: { next: number }
	): ReactNode {
		if (!isValidElement(node)) {
			return node;
		}

		const { children, ...props } = node.props as {
			children?: ReactNode;
		} & Record<string, unknown>;

		return createElement(
			node.type,
			{
				...props,
				key: `rich-${keyCounter.next++}`
			},
			children
		);
	}

	private parseRich(
		text: string,
		renderers: Record<string, (chunk: string) => ReactNode>,
		keyCounter: { next: number }
	): ReactNode[] {
		if (!text) {
			return [];
		}

		let processed = text;

		const selfClosingMap = new Map<string, ReactNode>();
		let selfIndex = 0;

		const selfClosingRegex = /<(\w+)\s*\/>/g;

		processed = processed.replace(selfClosingRegex, (_, tag: string) => {
			const renderer = renderers[tag];
			if (!renderer) {
				return '';
			}

			const placeholder = `__SELF_${selfIndex}__`;
			selfClosingMap.set(
				placeholder,
				this.withUniqueKey(renderer(''), keyCounter)
			);
			selfIndex++;

			return placeholder;
		});

		const parts: ReactNode[] = [];
		let lastIndex = 0;

		const pairedRegex = /<(\w+)>(.*?)<\/\1>/gs;
		let match: RegExpExecArray | null = pairedRegex.exec(processed);

		while (match !== null) {
			const full = match[0];
			const tag = match[1];
			const content = match[2];
			const index = match.index;

			if (index > lastIndex) {
				parts.push(processed.slice(lastIndex, index));
			}

			const renderer = renderers[tag];

			if (renderer) {
				let restoredContent = content;

				selfClosingMap.forEach((_, placeholder) => {
					if (restoredContent.includes(placeholder)) {
						restoredContent = restoredContent.replace(placeholder, '');
					}
				});

				const nestedChildren = this.parseRich(
					restoredContent,
					renderers,
					keyCounter
				);
				const rendered = renderer('');

				if (isValidElement(rendered)) {
					parts.push(
						createElement(
							rendered.type,
							{
								...(rendered.props as Record<string, unknown>),
								key: `rich-${keyCounter.next++}`
							},
							...nestedChildren
						)
					);
				} else {
					parts.push(...nestedChildren);
				}
			} else {
				parts.push(content);
			}

			lastIndex = index + full.length;
			match = pairedRegex.exec(processed);
		}

		if (lastIndex < processed.length) {
			parts.push(processed.slice(lastIndex));
		}

		const final: ReactNode[] = [];

		parts.forEach((part) => {
			if (typeof part === 'string') {
				const split = part.split(/(__SELF_\d+__)/g);

				split.forEach((segment) => {
					if (selfClosingMap.has(segment)) {
						final.push(selfClosingMap.get(segment) as ReactNode);
					} else if (segment) {
						final.push(segment);
					}
				});
			} else {
				final.push(part);
			}
		});

		return final;
	}
}

export const LocalizationService = new LocalizationServiceImpl();
export type { LocalizationServiceStateSnapshot };
